import { useEffect, useState } from "react";
import { CloudArrowUp, WarningCircle } from "@phosphor-icons/react";
import { getAll, subscribeQueue, drainQueue } from "@/lib/offlineQueue";

/**
 * OfflineQueuePill — persistent status pill for the offline upload queue.
 *
 * Renders NOTHING when the queue is empty (per user pref: only show when
 * there's actually queued work). Otherwise shows "N queued" with an
 * inline Retry-now button. Auto-refreshes on queue change and every 5s
 * while items remain.
 *
 * Meant to be dropped into WorkerJobViewV4's header so techs always see
 * the pending count wherever they are in the WO. Also renders inside
 * WorkerPhotoLightbox above the action bar.
 */
export default function OfflineQueuePill({ compact = false }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);

  const reload = async () => {
    try { setItems(await getAll()); } catch { /* IDB might not be ready — noop */ }
  };

  useEffect(() => {
    reload();
    const unsub = subscribeQueue(reload);
    const iv = setInterval(reload, 5000);
    const onOn = () => setOnline(true);
    const onOff = () => setOnline(false);
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    return () => {
      unsub();
      clearInterval(iv);
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, []);

  const retry = async () => {
    setBusy(true);
    try { await drainQueue(); } finally { setBusy(false); reload(); }
  };

  if (items.length === 0 && online) return null;

  // Offline banner even when empty — techs need to know
  if (items.length === 0 && !online) {
    return (
      <div
        data-testid="offline-pill-offline"
        className={`inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-200 px-3 h-8 text-[10px] font-black uppercase tracking-widest ${compact ? "" : ""}`}
        title="You're offline — new photos will queue until signal returns"
      >
        <WarningCircle size={12} weight="fill" /> Offline
      </div>
    );
  }

  // Count breakdown by kind for a friendlier label
  const byKind = items.reduce((acc, it) => {
    acc[it.kind] = (acc[it.kind] || 0) + 1;
    return acc;
  }, {});
  const label = (() => {
    const parts = [];
    if (byKind.photo) parts.push(`${byKind.photo} photo${byKind.photo === 1 ? "" : "s"}`);
    if (byKind.markup) parts.push(`${byKind.markup} markup`);
    if (byKind.voice_note) parts.push(`${byKind.voice_note} voice`);
    if (byKind.caption) parts.push(`${byKind.caption} caption`);
    return parts.join(" · ") || `${items.length} queued`;
  })();

  const totalAttempts = items.reduce((n, it) => n + (it.attempts || 0), 0);

  return (
    <div
      data-testid="offline-pill"
      className={`inline-flex items-center gap-2 rounded-full border px-3 h-8 text-[10px] font-black uppercase tracking-widest transition
        ${online ? "bg-[#00C4CC]/15 border-[#00C4CC]/40 text-[#0e5c5f]" : "bg-amber-500/15 border-amber-500/40 text-amber-200"}`}
      title={items.map(i => `${i.kind} · attempts ${i.attempts}${i.last_error ? " · " + i.last_error : ""}`).join("\n")}
    >
      <CloudArrowUp size={13} weight="fill" className={busy ? "animate-pulse" : ""} />
      <span data-testid="offline-pill-label">{label}</span>
      {!online && <span className="text-[9px] text-amber-200">· offline</span>}
      {totalAttempts > 0 && online && <span className="text-[9px] text-slate-500">· {totalAttempts} tries</span>}
      {online && (
        <button
          onClick={retry}
          disabled={busy}
          data-testid="offline-pill-retry"
          className="h-6 px-2 rounded-full bg-white/70 hover:bg-white text-[#0F1F3A] text-[9px] font-black uppercase tracking-widest disabled:opacity-50"
        >
          {busy ? "…" : "Retry"}
        </button>
      )}
    </div>
  );
}
